<template>
  <v-card>
    <v-card-title>Maschine erstellen</v-card-title>

    <v-form @submit.prevent="">
      <v-card-text>
        <v-text-field
          v-model="form.name"
          :label="$t(`entities.machine.fields.name`)" />
        <v-text-field
          v-model="form.category_machine"
          :label="$t(`entities.machine.fields.category_machine`)" />

        <v-text-field
          v-model="form.type_machine"
          :label="$t(`entities.machine.fields.type_machine`)" />

        <v-text-field
          v-model="form.id_number_machine"
          :label="$t(`entities.machine.fields.id_number_machine`)" />

        <v-row>
          <v-col
            cols="6"
            class="mb-10">
            <QuillEditor
              :content="form.comment"
              theme="snow"
              content-type="html"
              :label="$t(`entities.machine.fields.comment`)"
              :height="300" />
          </v-col>
          <v-col cols="6"
            ><CarouselWithUpload
              ref="uploadRef"
              field="basic-data-machine"
              :node-id="props.id"
              :upload-later="!props.id"
              space-id="null" />
          </v-col>
        </v-row>
      </v-card-text>

      <v-row no-gutters>
        <v-col cols="4">
          <v-card-title>{{ t(`entities.machine.tabs.fixCost`) }}</v-card-title>
        </v-col>
        <v-col cols="4"></v-col>
        <v-col cols="4">
          <v-card-title>{{
            t(`entities.machine.tabs.variableCost`)
          }}</v-card-title>
        </v-col>
        <v-col cols="4">
          <v-card-text>
            <v-text-field
              v-model="form.acquisition_costs_machine"
              :label="$t(`entities.machine.fields.acquisition_costs_machine`)"
              :rules="[required, positive]" />

            <v-text-field
              v-model="form.installation_and_transportation_costs_machine"
              :label="
                $t(
                  `entities.machine.fields.installation_and_transportation_costs_machine`
                )
              "
              :rules="[positive]" />

            <v-text-field
              v-model="form.special_foundation_costs_machine"
              :label="
                $t(`entities.machine.fields.special_foundation_costs_machine`)
              "
              :rules="[positive]" />

            <v-text-field
              v-model="form.other_costs_machine"
              :label="$t(`entities.machine.fields.other_costs_machine`)"
              :rules="[positive]" />

            <v-text-field
              v-model="form.residual_value_machine"
              :label="$t(`entities.machine.fields.residual_value_machine`)"
              :rules="[positive]" />

            <v-text-field
              v-model="form.useful_life_machine"
              :label="$t(`entities.machine.fields.useful_life_machine`)"
              :rules="[required, positive]" />

            <v-text-field
              v-model="form.required_space_including_auxiliary_areas_machine"
              :label="
                $t(
                  `entities.machine.fields.required_space_including_auxiliary_areas_machine`
                )
              "
              :rules="[positive]" />
          </v-card-text>
        </v-col>
        <v-col cols="4">
          <v-card-text>
            <v-text-field
              v-model="form.replacement_costs_machine"
              :label="$t(`entities.machine.fields.replacement_costs_machine`)"
              :rules="[required, positive]" />

            <v-text-field
              v-model="
                form.future_installation_and_transportation_costs_machine
              "
              :label="
                $t(
                  `entities.machine.fields.future_installation_and_transportation_costs_machine`
                )
              "
              :rules="[positive]" />

            <v-text-field
              v-model="form.future_special_foundation_costs_machine"
              :label="
                $t(
                  `entities.machine.fields.future_special_foundation_costs_machine`
                )
              "
              :rules="[positive]" />

            <v-text-field
              v-model="form.future_disposal_costs_machine"
              :label="
                $t(`entities.machine.fields.future_disposal_costs_machine`)
              "
              :rules="[positive]" />

            <v-text-field
              v-model="form.future_other_costs_machine"
              :label="$t(`entities.machine.fields.future_other_costs_machine`)"
              :rules="[positive]" />

            <v-text-field
              v-model="form.future_residual_value_machine"
              :label="
                $t(`entities.machine.fields.future_residual_value_machine`)
              "
              :rules="[positive]" />
          </v-card-text>
        </v-col>
        <v-col cols="4">
          <v-card-text>
            <v-text-field
              v-model="form.maintenance_costs_year_machine"
              :label="
                $t(`entities.machine.fields.maintenance_costs_year_machine`)
              "
              :rules="[required, positive]" />

            <v-text-field
              v-model="form.connected_load_machine"
              :label="$t(`entities.machine.fields.connected_load_machine`)"
              :rules="[required, positive]" />

            <v-text-field
              v-model="form.power_consumption_machine"
              :label="$t(`entities.machine.fields.power_consumption_machine`)"
              :rules="[required, positive]" />

            <v-text-field
              v-model="form.operating_material_costs_year_machine"
              :label="
                $t(
                  `entities.machine.fields.operating_material_costs_year_machine`
                )
              "
              :rules="[required, positive]" />
          </v-card-text>
        </v-col>
      </v-row>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="save">{{ t(`actions.cancel`) }}</v-btn>
        <v-btn @click="save">{{ t(`actions.save`) }}</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script setup lang="ts">
import CarouselWithUpload from "@/components/Elements/CarouselWithUpload.vue";
import { onMounted, onUnmounted, ref, useTemplateRef } from "vue";
import { useTranslation } from "i18next-vue";
import { useCommonRules } from "@/validations/common";
import axios from "axios";

const { t } = useTranslation();
const { required, positive } = useCommonRules(t);

const emit = defineEmits(["save"]);

const props = defineProps({
  id: {
    type: Number,
    required: false
  }
});

const uploadRef = useTemplateRef("uploadRef");

onMounted(async () => {
  if (props.id) {
    form.value = await axios
      .get(`/api/database/machine/${props.id}`)
      .then((response) => response.data);
  }
});

const form = ref({
  name: "",
  category_machine: "",
  type_machine: "",
  id_number_machine: "",
  comment: "",
  acquisition_costs_machine: "",
  installation_and_transportation_costs_machine: "",
  special_foundation_costs_machine: "",
  other_costs_machine: "",
  residual_value_machine: "",
  replacement_costs_machine: "",
  future_installation_and_transportation_costs_machine: "",
  future_special_foundation_costs_machine: "",
  future_disposal_costs_machine: "",
  future_other_costs_machine: "",
  future_residual_value_machine: "",
  useful_life_machine: "",
  required_space_including_auxiliary_areas_machine: "",
  maintenance_costs_year_machine: "",
  connected_load_machine: "",
  power_consumption_machine: "",
  operating_material_costs_year_machine: ""
});

function save() {
  if (props.id) {
    axios
      .put(`/api/database/machine/${props.id}`, form.value)
      .then((response) => {
        console.log(response);
        console.log("uploadFiles");
        uploadRef.value.uploadAcceptedFiles(props.id);
        emit("save");
      });
    return;
  }
  axios.post("/api/database/machine", form.value).then((response) => {
    console.log(response);
    console.log("uploadFiles");
    uploadRef.value.uploadAcceptedFiles(response.data.id);
    emit("save");
  });
}
</script>

<style scoped>
.v-text-field {
  margin-bottom: 16px;
}
</style>
